'use client'
import Link from 'next/link'
import { List } from '@phosphor-icons/react'
import { X } from '@/components/icons/glyphs'
import { Button } from '@/components/ui/button'
import React from 'react'
import Image from 'next/image'
import { ModeToggle } from '@/components/theme/theme-toggle'
import { FOCUS_RING, PRIMARY_CTA } from '@/components/landing/cta'
import { cn } from '@/lib/utils'

export const Logo = (props: React.SVGAttributes<SVGElement>) => {
  return (
    <svg
      width="1em"
      height="1em"
      viewBox="0 0 324 323"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <rect
        x="0.1023"
        y="144.792"
        width="350.802"
        height="36.5788"
        rx="18.2894"
        transform="rotate(-60.5799 88.1023 144.792)"
        fill="currentColor"
      />
      <rect
        x="85.3459"
        y="244.537"
        width="151.802"
        height="36.5788"
        rx="18.2894"
        transform="rotate(38.5799 250.3459 350.537)"
        fill="currentColor"
      />
    </svg>
  )
}

const menuItems = [
  { name: 'Features', href: '#features' },
  { name: 'Pricing', href: '#pricing' },
  { name: 'FAQ', href: '#faq' },
]

export const HeroHeader = ({ isSignedIn }: { isSignedIn: boolean }) => {
  const [menuOpen, setMenuOpen] = React.useState(false)
  const closeMenu = () => setMenuOpen(false)

  return (
    <header className="sticky top-0 z-30 border-b border-border/70 bg-background/85 pt-[env(safe-area-inset-top)] backdrop-blur-md">
      <nav
        aria-label="Main"
        className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-6 px-5 md:px-8"
      >
        <div className="flex items-center gap-10">
          <Link href="/" aria-label="Haven home" className={cn(FOCUS_RING, 'flex min-h-11 items-center gap-2 rounded-lg')}>
            <Image src="/favicons/haven-rounded.png" alt="" width={28} height={28} className="rounded-lg" />
            <span className="text-lg font-semibold">Haven</span>
          </Link>
          <ul className="hidden items-center gap-7 text-sm md:flex">
            {menuItems.map((item) => (
              <li key={item.href}>
                <Link
                  href={item.href}
                  className={cn(FOCUS_RING, 'inline-flex min-h-11 items-center rounded-md text-muted-foreground transition-colors duration-150 hover:text-foreground')}
                >
                  {item.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="hidden items-center gap-3 md:flex">
          <ModeToggle className="size-11 rounded-full" />
          {isSignedIn ? (
            <Button asChild variant="outline" className="h-11 rounded-full px-5">
              <Link href="/dashboard">Dashboard</Link>
            </Button>
          ) : (
            <>
              <Link
                href="/sign-in"
                className={cn(FOCUS_RING, 'inline-flex min-h-11 items-center rounded-md px-2 text-sm text-muted-foreground transition-colors duration-150 hover:text-foreground')}
              >
                Log in
              </Link>
              <Button asChild className={cn('h-11 px-5', PRIMARY_CTA)}>
                <Link href="/sign-up">Get started free</Link>
              </Button>
            </>
          )}
        </div>

        <div className="flex items-center gap-2 md:hidden">
          <ModeToggle className="size-11 rounded-full" />
          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            aria-expanded={menuOpen}
            aria-controls="mobile-menu"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            className={cn(FOCUS_RING, '-mr-2 flex size-11 items-center justify-center rounded-full text-foreground')}
          >
            {menuOpen ? <X className="size-5" /> : <List className="size-5" />}
          </button>
        </div>
      </nav>

      {menuOpen && (
        <div id="mobile-menu" className="border-t border-border px-5 pb-6 pt-2 md:hidden">
          <ul className="divide-y divide-border">
            {menuItems.map((item) => (
              <li key={item.href}>
                <Link
                  href={item.href}
                  onClick={closeMenu}
                  className={cn(FOCUS_RING, 'flex min-h-12 items-center rounded-md text-base text-foreground')}
                >
                  {item.name}
                </Link>
              </li>
            ))}
          </ul>
          <div className="mt-4 grid gap-3">
            {isSignedIn ? (
              <Button asChild variant="outline" className="h-12 rounded-full text-base">
                <Link href="/dashboard" onClick={closeMenu}>Dashboard</Link>
              </Button>
            ) : (
              <>
                <Button asChild className={cn('h-12 text-base', PRIMARY_CTA)}>
                  <Link href="/sign-up" onClick={closeMenu}>Get started free</Link>
                </Button>
                <Button asChild variant="outline" className="h-12 rounded-full text-base">
                  <Link href="/sign-in" onClick={closeMenu}>Log in</Link>
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  )
}
